<script>
  import { onMount } from 'svelte';
  import { COLORS, clearCanvas } from './chartUtils.js';
  import { formatSignedPercent } from '../../../lib/strategyResult.js';

  // Monthly-returns heatmap: { years: [..], months: [1..12], matrix: [[cell|null,..],..] } (fractions).
  // year Ã month color grid (new Canvas grammar vs the first 3). null cells render BLANK and are excluded
  // from the color domain (KTD). The highest-value of the three optional charts (built per Scope).
  export let data = { years: [], months: [], matrix: [] };

  const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

  let canvas;
  let ctx;
  let container;
  let width = 600;
  let height = 200;
  let hover = null; // { r, c }

  $: years = (data && data.years) || [];
  $: months = (data && data.months) || [];
  $: matrix = (data && data.matrix) || [];
  $: hasData = years.length >= 1 && matrix.length >= 1;

  // Color domain excludes null cells; symmetric around zero so 0 = neutral.
  $: domain = (() => {
    let mag = 0;
    for (const row of matrix) for (const cell of row) {
      if (cell !== null && cell !== undefined) mag = Math.max(mag, Math.abs(cell));
    }
    return mag === 0 ? 1 : mag;
  })();

  $: summary = hasData
    ? `Monthly returns heatmap across ${years.length} year(s).`
    : 'Monthly returns heatmap: insufficient data.';

  const LABEL_W = 44; // left gutter for year labels
  const HEADER_H = 16; // top row for month labels

  function resize() {
    if (!container) return;
    width = Math.max(200, container.offsetWidth);
    // Height scales with the year count so cells stay roughly square but bounded.
    const cellH = Math.max(16, Math.min(28, (width - LABEL_W) / 12));
    height = HEADER_H + cellH * Math.max(1, years.length) + 8;
  }

  // Diverging greenâred color for a fractional return, scaled by the symmetric domain.
  function cellColor(v) {
    const t = Math.max(-1, Math.min(1, v / domain));
    const alpha = 0.18 + 0.62 * Math.abs(t);
    return v >= 0
      ? `rgba(0, 255, 136, ${alpha.toFixed(3)})`   // gain
      : `rgba(255, 59, 59, ${alpha.toFixed(3)})`;  // loss
  }

  function draw() {
    if (!ctx || !hasData) return;
    clearCanvas(ctx, width, height);
    const gridW = width - LABEL_W;
    const cellW = gridW / 12;
    const cellH = (height - HEADER_H - 8) / Math.max(1, years.length);

    ctx.font = '10px sans-serif';
    ctx.textBaseline = 'middle';

    // Month headers.
    ctx.fillStyle = COLORS.label;
    ctx.textAlign = 'center';
    for (let c = 0; c < 12; c++) {
      ctx.fillText(MONTH_LABELS[c], LABEL_W + cellW * (c + 0.5), HEADER_H / 2);
    }

    for (let r = 0; r < years.length; r++) {
      // Year label.
      ctx.fillStyle = COLORS.label;
      ctx.textAlign = 'right';
      ctx.fillText(String(years[r]), LABEL_W - 6, HEADER_H + cellH * (r + 0.5));

      const row = matrix[r] || [];
      for (let c = 0; c < 12; c++) {
        const cell = row[c];
        const x = LABEL_W + cellW * c;
        const y = HEADER_H + cellH * r;
        if (cell === null || cell === undefined) continue; // absent â blank (excluded from domain)

        ctx.fillStyle = cellColor(cell);
        ctx.fillRect(x + 1, y + 1, cellW - 2, cellH - 2);

        // Hovered cell outline + value.
        if (hover && hover.r === r && hover.c === c) {
          ctx.strokeStyle = '#e8e9ea';
          ctx.lineWidth = 1.5;
          ctx.strokeRect(x + 1, y + 1, cellW - 2, cellH - 2);
        }
      }
    }
  }

  $: if (ctx && canvas && hasData && width && height && matrix && (hover || hover === null)) draw();

  function cellAt(px, py) {
    const gridW = width - LABEL_W;
    const cellW = gridW / 12;
    const cellH = (height - HEADER_H - 8) / Math.max(1, years.length);
    if (px < LABEL_W || py < HEADER_H) return null;
    const c = Math.floor((px - LABEL_W) / cellW);
    const r = Math.floor((py - HEADER_H) / cellH);
    if (r < 0 || r >= years.length || c < 0 || c >= 12) return null;
    return { r, c };
  }

  let liveMsg = '';
  function onMove(e) {
    if (!hasData) return;
    const rect = canvas.getBoundingClientRect();
    hover = cellAt(e.clientX - rect.left, e.clientY - rect.top);
    if (hover) {
      const cell = (matrix[hover.r] || [])[hover.c];
      liveMsg = cell === null || cell === undefined
        ? `${MONTH_LABELS[hover.c]} ${years[hover.r]}: no data`
        : `${MONTH_LABELS[hover.c]} ${years[hover.r]}: ${formatSignedPercent(cell)}`;
    }
  }
  function onLeave() { hover = null; }

  // Lazy 2D-context acquisition: the canvas renders inside {#if hasData}, so it may not exist at
  // mount (empty data — acquiring unconditionally threw and broke the whole reactive graph) and may
  // APPEAR later when data arrives. Acquire the context whenever the canvas binds.
  $: if (canvas && !ctx) { ctx = canvas.getContext('2d'); resize(); }

  onMount(() => {
    if (!canvas) return; // not rendered (empty state) — nothing to wire up
    if (canvas) ctx = canvas.getContext('2d');
    resize();
    draw();
    const onResize = () => { resize(); draw(); };
    window.addEventListener('resize', onResize);
    let pendingDraw;
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting && ctx && canvas) {
          clearTimeout(pendingDraw);
          pendingDraw = setTimeout(() => { resize(); draw(); }, 50);
        }
      });
    }, { threshold: 0.1 });
    if (container) observer.observe(container);
    return () => { window.removeEventListener('resize', onResize); observer.disconnect(); clearTimeout(pendingDraw); };
  });
</script>

<div class="bt-chart" bind:this={container}>
  <h4 class="bt-chart-title">Monthly Returns</h4>
  {#if hasData}
    <canvas
      bind:this={canvas}
      width={width}
      height={height}
      tabindex="0"
      aria-label={summary}
      on:mousemove={onMove}
      on:mouseleave={onLeave}
    ></canvas>
    <div class="bt-chart-live" aria-live="polite">{liveMsg}</div>
    <details class="bt-chart-data">
      <summary>View data as table</summary>
      <table>
        <thead>
          <tr><th>Year</th>{#each MONTH_LABELS as m}<th>{m}</th>{/each}</tr>
        </thead>
        <tbody>
          {#each years as y, r}
            <tr>
              <td>{y}</td>
              {#each MONTH_LABELS as _, c}
                <td>{matrix[r] && matrix[r][c] !== null && matrix[r][c] !== undefined ? formatSignedPercent(matrix[r][c]) : ''}</td>
              {/each}
            </tr>
          {/each}
        </tbody>
      </table>
    </details>
  {:else}
    <div class="empty-state"><p class="empty-state-description">Insufficient data for this chart.</p></div>
  {/if}
</div>

<style>
  @import './charts.css';
</style>
